import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, Clock, User, DollarSign, FileText, MapPin } from 'lucide-react';
import { parseLocalDate } from '../lib/utils';


const CustomBooking = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expired, setExpired] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [paymentOption, setPaymentOption] = useState('full'); // 'full' or 'deposit'

  useEffect(() => {
    fetchBookingDetails();
  }, [token]);

  const fetchBookingDetails = async () => {
    try {
      const response = await axios.get(
        `/api/bookings/custom/${token}`
      );

      if (response.data.expired) {
        setExpired(true);
        setErrorMessage(response.data.message);
      } else {
        setBooking(response.data.booking);
      }
    } catch (error) {
      console.error('Error fetching booking:', error);
      setExpired(true);
      setErrorMessage('Unable to load booking. This link may be invalid or expired.');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!booking) return;

    setProcessing(true);
    const payAmount = paymentOption === 'deposit' ? Math.ceil(booking.price * 0.5 * 100) / 100 : booking.price;

    try {
      // Create Stripe checkout session
      const response = await axios.post(
        `/api/payments/create-checkout`,
        {
          amount: payAmount,
          service_name: booking.service_name,
          customer_email: booking.client_email,
          customer_name: booking.client_name,
          booking_type: 'custom',
          custom_booking_token: token,
          payment_option: paymentOption,
          metadata: {
            booking_token: token,
            engineer_id: booking.engineer_id,
            engineer_name: booking.engineer_name,
            date: booking.date,
            time: booking.time,
            payment_option: paymentOption,
            total_price: booking.price
          }
        }
      );

      if (response.data.url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.url;
      } else {
        toast.error('Failed to create payment session');
        setProcessing(false);
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Payment failed. Please try again.');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-black to-blue-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-purple-500 mx-auto"></div>
          <p className="text-white mt-4 text-lg">Loading booking details...</p>
        </div>
      </div>
    );
  }

  if (expired) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-black to-blue-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Booking Link Expired</h1>
          <p className="text-gray-600 mb-6">{errorMessage}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  if (!booking) {
    return null;
  }

  const timeRemaining = new Date(booking.token_expires_at) - new Date();
  const hoursRemaining = Math.floor(timeRemaining / (1000 * 60 * 60));
  const minutesRemaining = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-black to-blue-900 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">🎵</div>
          <h1 className="text-4xl font-bold text-white mb-2">
            Your Studio Session Awaits
          </h1>
          <p className="text-purple-300">
            Review your booking details and complete payment
          </p>
        </div>

        {/* Rewards Banner */}
        <div className="bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg p-4 mb-6 shadow-xl">
          <div className="flex items-center gap-3">
            <span className="text-4xl">🎁</span>
            <div className="flex-1">
              <p className="text-white font-bold text-lg">
                Create a Free Account & Earn Rewards!
              </p>
              <p className="text-white/90 text-sm">
                Earn 1 credit for every 3 hours booked • Redeem 3 credits for 1 FREE hour
              </p>
            </div>
            <a
              href="/"
              className="bg-white text-orange-600 px-4 py-2 rounded-lg font-semibold hover:bg-orange-50 transition-colors text-sm whitespace-nowrap"
            >
              Sign Up Free
            </a>
          </div>
        </div>

        {/* Expiration Warning */}
        {timeRemaining > 0 && (
          <div className="bg-yellow-900/30 border border-yellow-500/50 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <span className="text-2xl">⏰</span>
              <div>
                <p className="text-yellow-200 font-semibold">
                  Link Expires in {hoursRemaining}h {minutesRemaining}m
                </p>
                <p className="text-yellow-300/70 text-sm">
                  Complete your booking before the link expires
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Booking Details Card */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl p-6 md:p-8 border border-purple-500/30 mb-6">
          <h2 className="text-2xl font-bold text-white mb-6">Session Details</h2>
          
          <div className="space-y-4">
            {/* Service */}
            <div className="flex items-start gap-4 p-4 bg-purple-900/20 rounded-lg">
              <FileText className="text-purple-400 mt-1" size={24} />
              <div>
                <p className="text-gray-400 text-sm">Service</p>
                <p className="text-white font-semibold text-lg">{booking.service_name}</p>
              </div>
            </div>

            {/* Engineer */}
            <div className="flex items-start gap-4 p-4 bg-purple-900/20 rounded-lg">
              <User className="text-purple-400 mt-1" size={24} />
              <div>
                <p className="text-gray-400 text-sm">Engineer</p>
                <p className="text-white font-semibold text-lg">{booking.engineer_name}</p>
              </div>
            </div>

            {/* Date */}
            <div className="flex items-start gap-4 p-4 bg-purple-900/20 rounded-lg">
              <Calendar className="text-purple-400 mt-1" size={24} />
              <div>
                <p className="text-gray-400 text-sm">Date</p>
                <p className="text-white font-semibold text-lg">
                  {parseLocalDate(booking.date).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
            </div>

            {/* Time */}
            <div className="flex items-start gap-4 p-4 bg-purple-900/20 rounded-lg">
              <Clock className="text-purple-400 mt-1" size={24} />
              <div>
                <p className="text-gray-400 text-sm">Time</p>
                <p className="text-white font-semibold text-lg">{booking.time}</p>
              </div>
            </div>

            {/* Location */}
            <div className="flex items-start gap-4 p-4 bg-purple-900/20 rounded-lg">
              <MapPin className="text-purple-400 mt-1" size={24} />
              <div>
                <p className="text-gray-400 text-sm">Location</p>
                <p className="text-white font-semibold">270 Meserole Street</p>
                <p className="text-gray-400 text-sm">Brooklyn, NY 11206</p>
              </div>
            </div>

            {/* Notes */}
            {booking.notes && (
              <div className="flex items-start gap-4 p-4 bg-blue-900/20 rounded-lg border border-blue-500/30">
                <FileText className="text-blue-400 mt-1" size={24} />
                <div>
                  <p className="text-gray-400 text-sm">Special Notes</p>
                  <p className="text-white">{booking.notes}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Payment Options */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl p-6 md:p-8 mb-6 border border-purple-500/30">
          <h2 className="text-xl font-bold text-white mb-4">Choose Payment Option</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Pay in Full */}
            <button
              onClick={() => setPaymentOption('full')}
              className={`p-5 rounded-xl border-2 transition-all text-left ${
                paymentOption === 'full'
                  ? 'border-green-500 bg-green-500/10'
                  : 'border-gray-600 bg-gray-800/30 hover:border-gray-500'
              }`}
              data-testid="pay-full-option"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-white font-bold text-lg">Pay in Full</span>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  paymentOption === 'full' ? 'border-green-500 bg-green-500' : 'border-gray-500'
                }`}>
                  {paymentOption === 'full' && <div className="w-2 h-2 bg-white rounded-full" />}
                </div>
              </div>
              <p className="text-green-400 font-bold text-2xl">${booking.price.toFixed(2)}</p>
              <p className="text-gray-400 text-xs mt-1">No balance due at session</p>
            </button>

            {/* Pay Deposit */}
            <button
              onClick={() => setPaymentOption('deposit')}
              className={`p-5 rounded-xl border-2 transition-all text-left ${
                paymentOption === 'deposit'
                  ? 'border-purple-500 bg-purple-500/10'
                  : 'border-gray-600 bg-gray-800/30 hover:border-gray-500'
              }`}
              data-testid="pay-deposit-option"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-white font-bold text-lg">Pay Deposit</span>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  paymentOption === 'deposit' ? 'border-purple-500 bg-purple-500' : 'border-gray-500'
                }`}>
                  {paymentOption === 'deposit' && <div className="w-2 h-2 bg-white rounded-full" />}
                </div>
              </div>
              <p className="text-purple-400 font-bold text-2xl">${(Math.ceil(booking.price * 0.5 * 100) / 100).toFixed(2)}</p>
              <p className="text-gray-400 text-xs mt-1">${(booking.price - Math.ceil(booking.price * 0.5 * 100) / 100).toFixed(2)} balance due at session</p>
            </button>
          </div>
        </div>

        {/* Payment Button */}
        <button
          onClick={handlePayment}
          disabled={processing}
          className="w-full bg-white text-purple-900 py-4 px-8 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-2xl flex items-center justify-center gap-3"
          data-testid="custom-booking-pay-btn"
        >
          {processing ? (
            <>
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-purple-900"></div>
              <span>Processing...</span>
            </>
          ) : (
            <>
              <span>Complete Booking & Pay ${paymentOption === 'deposit' ? (Math.ceil(booking.price * 0.5 * 100) / 100).toFixed(2) : booking.price.toFixed(2)}</span>
            </>
          )}
        </button>

        {/* Rewards Reminder */}
        <div className="mt-6 bg-purple-900/30 border border-purple-500/30 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <span className="text-2xl">💡</span>
            <div>
              <p className="text-purple-200 font-semibold mb-1">
                Don't Miss Out on Free Studio Time!
              </p>
              <p className="text-purple-300/80 text-sm">
                After booking, create a free account to automatically earn credits for this session. 
                The more you book, the more you earn! 
                <a href="/" className="text-yellow-300 hover:text-yellow-200 font-semibold ml-1">
                  Sign up now →
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* Info */}
        <div className="mt-6 text-center text-gray-400 text-sm">
          <p>🔒 Secure payment powered by Stripe</p>
          <p className="mt-2">Your booking will be confirmed immediately after payment</p>
        </div>
      </div>
    </div>
  );
};

export default CustomBooking;
